import { useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ChevronDown, ExternalLink, FileCheck, FolderOpen, MapPin, Navigation, Phone, Play, User } from 'lucide-react'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { useAuth } from '../../context/AuthContext'
import { getFacilities, type FacilityOption } from '../../lib/services/facilities'
import { listFacilityContacts } from '../../lib/services/facilityContacts'
import { getAssignments } from '../../lib/services/visits'
import { startSurvey } from '../../lib/services/survey'
import { surveyStore } from '../../lib/offline/surveyStore'
import { getDocumentRequests, getPrestudyDocuments, PRESTUDY_DOC_CATEGORIES } from '../../lib/services/prestudy'
import { fieldSurveyorSurveyActionLabel, fieldSurveyorSurveyDestination } from '../../lib/fieldSurveyorSurveyRouting'

function navUrl(address: string, provider: 'google' | 'waze' | 'apple'): string {
  const q = encodeURIComponent(address)
  if (provider === 'waze') return `https://waze.com/ul?q=${q}&navigate=yes`
  if (provider === 'apple') return `https://maps.apple.com/?q=${q}`
  return `https://www.google.com/maps/search/?api=1&query=${q}`
}

function buildAddress(f: FacilityOption): string {
  return [f.address, f.city, f.state, f.zip_code].filter(Boolean).join(', ')
}

function surveyFrameworkForFacility(facility: FacilityOption | null): string {
  const framework = facility?.accreditation_framework
  if (framework === 'aaahc') return 'AAAHC'
  if (framework === 'tjc') return 'TJC'
  if (framework === 'quad_a') return 'QUAD_A'
  if (framework === 'achc') return 'ACHC'
  if (framework === 'state') return 'STATE'
  return 'CMS'
}

export function SurveyorTodayPage() {
  const navigate = useNavigate()
  const { viewerContext, isLoading: authLoading } = useAuth()
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null)

  const { data: facilities = [], isLoading: loadingFacilities } = useQuery({
    queryKey: ['facilities'],
    queryFn: getFacilities,
  })

  const { data: visits = [], isLoading: loadingVisits } = useQuery({
    queryKey: ['assignments'],
    queryFn: getAssignments,
  })

  const isLoading = loadingFacilities || loadingVisits

  // Auto-select: first facility with an in-progress visit, else first facility
  const autoSelectedId = useMemo(() => {
    const inProgress = visits.find((v) => v.status === 'in_progress')
    if (inProgress?.facility_id) return inProgress.facility_id
    return facilities[0]?.facility_id ?? null
  }, [facilities, visits])

  const activeFacilityId = selectedFacilityId ?? autoSelectedId
  const facility = facilities.find((f) => f.facility_id === activeFacilityId) ?? null

  const { data: contacts = [] } = useQuery({
    queryKey: ['facility-contacts', activeFacilityId],
    queryFn: () => listFacilityContacts(activeFacilityId!),
    enabled: !!activeFacilityId,
  })
  const primaryContact = contacts.find((c) => c.is_primary) ?? contacts[0] ?? null

  // Visits for selected facility
  const facilityVisits = useMemo(
    () => visits.filter((v) => v.facility_id === activeFacilityId),
    [visits, activeFacilityId],
  )
  const activeVisit = facilityVisits.find((v) => v.status === 'in_progress') ?? null
  const scheduledVisit = facilityVisits.find((v) => v.status === 'scheduled') ?? null
  const hasActiveSurvey = !!activeVisit
  const startScheduledSurvey = useMutation({
    mutationFn: (visitId: string) => startSurvey(visitId, surveyFrameworkForFacility(facility)),
    onSuccess: async (session) => {
      await surveyStore.saveSession(session).catch(() => {})
      navigate(`/visits/${session.visit_id}/survey/walkthrough`)
    },
  })

  // Prestudy documents for the active or scheduled visit
  const currentVisitId = activeVisit?.id ?? scheduledVisit?.id ?? null
  const { data: docRequests = [] } = useQuery({
    queryKey: ['doc-requests', currentVisitId],
    queryFn: () => getDocumentRequests(currentVisitId!),
    enabled: !!currentVisitId,
  })
  const { data: prestudyDocs = [] } = useQuery({
    queryKey: ['prestudy-docs', currentVisitId],
    queryFn: () => getPrestudyDocuments(currentVisitId!),
    enabled: !!currentVisitId,
  })
  const requestedTypes = docRequests.flatMap((r) => r.requested_docs ?? [])
  const totalRequested = requestedTypes.length || PRESTUDY_DOC_CATEGORIES.length
  const totalUploaded = prestudyDocs.length

  // Needs action across ALL facilities
  const needsAction = useMemo(
    () => visits.filter((v) =>
      v.status === 'in_review'
      || v.status === 'released'
      || (v.status === 'in_progress' && v.id !== activeVisit?.id),
    ),
    [visits, activeVisit],
  )

  // HCP#1686 — /today is FieldSurveyor-only. Soft-open for console roles breaks
  // seeded denied-route probes (stay on /today forever).
  if (!authLoading && viewerContext && viewerContext.role !== 'FieldSurveyor') {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <FieldAppShell eyebrow="Field surveyor" title="Today" showBackControl={false}>
      <div className="space-y-3">
        {isLoading && (
          <p className="py-8 text-center text-sm text-slate-500">Loading…</p>
        )}

        {/* Facility selector */}
        {!isLoading && facilities.length > 0 && (
          <div className="relative">
            <select
              value={activeFacilityId ?? ''}
              onChange={(e) => setSelectedFacilityId(e.target.value || null)}
              className="fs-form-input w-full appearance-none pr-9 font-bold"
            >
              {facilities.map((f) => (
                <option key={f.facility_id} value={f.facility_id}>
                  {f.facility_name}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
          </div>
        )}

        {/* Map + navigation */}
        {facility && (
          <div className="fs-surface overflow-hidden">
            <div
              className="flex h-36 items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #e8f2fb, #dde8f3)' }}
            >
              <div className="text-center">
                <MapPin size={28} className="mx-auto mb-1 text-[var(--brand-primary)]" />
                <p className="text-[11px] font-bold text-[var(--brand-deep)]">{facility.facility_name}</p>
                {buildAddress(facility) && (
                  <p className="mt-0.5 text-[10px] text-slate-500">{buildAddress(facility)}</p>
                )}
              </div>
            </div>
            <div className="flex divide-x" style={{ borderTop: '1px solid rgba(23,86,141,0.16)', borderColor: 'rgba(23,86,141,0.16)' }}>
              <a
                href={navUrl(buildAddress(facility), 'google')}
                target="_blank"
                rel="noreferrer"
                className="flex flex-1 items-center justify-center gap-1.5 py-2.5 text-[11px] font-bold text-[var(--brand-primary)] hover:bg-slate-50"
              >
                <Navigation size={13} />
                Google Maps
              </a>
              <a
                href={navUrl(buildAddress(facility), 'waze')}
                target="_blank"
                rel="noreferrer"
                className="flex flex-1 items-center justify-center gap-1.5 py-2.5 text-[11px] font-bold text-[var(--brand-primary)] hover:bg-slate-50"
              >
                <ExternalLink size={13} />
                Waze
              </a>
              <a
                href={navUrl(buildAddress(facility), 'apple')}
                target="_blank"
                rel="noreferrer"
                className="flex flex-1 items-center justify-center gap-1.5 py-2.5 text-[11px] font-bold text-[var(--brand-primary)] hover:bg-slate-50"
              >
                <Navigation size={13} />
                Apple Maps
              </a>
            </div>
          </div>
        )}

        {/* Contact + documents card */}
        {facility && (
          <div className="fs-surface p-3.5">
            {primaryContact ? (
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--brand-light)] text-[var(--brand-primary)]">
                  <User size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="fs-row-title">{primaryContact.contact_name}</p>
                  {primaryContact.role_label && (
                    <p className="fs-row-meta">{primaryContact.role_label}</p>
                  )}
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {primaryContact.phone && (
                      <a
                        href={`tel:${primaryContact.phone}`}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-[var(--brand-primary)]"
                      >
                        <Phone size={11} />
                        {primaryContact.phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-[11px] text-slate-500">No contact on file</p>
            )}
          </div>
        )}

        {/* Selected facility info card */}
        {facility && (
          <div className="fs-surface p-3.5">
            <p className="font-brand text-[15px] font-bold leading-tight text-[var(--brand-deep)]">
              {facility.facility_name}
            </p>
            {buildAddress(facility) && (
              <p className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-500">
                <MapPin size={11} className="shrink-0" />
                {buildAddress(facility)}
              </p>
            )}
            {(facility.facility_type || facility.license_number) && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {facility.facility_type && (
                  <span className="brand-chip">{facility.facility_type}</span>
                )}
                {facility.license_number && (
                  <span className="fs-tag fs-tag-gray">License {facility.license_number}</span>
                )}
              </div>
            )}

            {/* Prestudy documents */}
            {currentVisitId && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => navigate(`/visits/${currentVisitId}/survey/documents`)}
                  className="flex w-full items-center gap-3 rounded-[11px] border border-slate-200 bg-white px-3 py-2.5 text-left hover:bg-slate-50"
                >
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]"
                    style={{ background: 'var(--brand-light)' }}
                  >
                    <FolderOpen size={18} className="text-[var(--brand-primary)]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-bold text-slate-900">View uploaded facility documents</p>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      {totalUploaded} of {totalRequested} documents uploaded
                    </p>
                  </div>
                  {totalUploaded >= totalRequested && totalRequested > 0 ? (
                    <FileCheck size={16} className="shrink-0 text-emerald-600" />
                  ) : (
                    <span className="fs-tag fs-tag-action">{totalRequested - totalUploaded} pending</span>
                  )}
                </button>
              </div>
            )}

            {/* Survey action */}
            <div className="mt-3">
              {hasActiveSurvey ? (
                <>
                  <p className="mb-2 text-[11px] text-slate-500">
                    Survey in progress · {activeVisit.visit_date}
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate(`/visits/${activeVisit.id}/survey/walkthrough`)}
                    className="fs-btn-primary"
                  >
                    Resume survey
                  </button>
                </>
              ) : scheduledVisit ? (
                <>
                  <p className="mb-2 text-[11px] text-slate-500">
                    Scheduled · {scheduledVisit.visit_date}
                  </p>
                  <button
                    type="button"
                    onClick={() => startScheduledSurvey.mutate(scheduledVisit.id)}
                    disabled={startScheduledSurvey.isPending}
                    className="fs-btn-primary"
                  >
                    <Play size={15} />
                    {startScheduledSurvey.isPending ? 'Starting...' : 'Start survey'}
                  </button>
                </>
              ) : (
                <>
                  <p className="mb-2 text-[11px] text-slate-500">
                    No active or scheduled survey
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate(`/visits/new?facilityId=${facility.facility_id}`)}
                    className="fs-btn-primary"
                  >
                    <Play size={15} />
                    Start new survey
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Needs action across all facilities */}
        {needsAction.length > 0 && (
          <div className="fs-surface p-3.5">
            <p className="fs-label mb-2">Needs action</p>
            <div className="space-y-1">
              {needsAction.slice(0, 5).map((v) => {
                const meta = v.status === 'in_review' || v.status === 'released'
                  ? `Completed ${v.visit_date}`
                  : `In progress · ${v.visit_date}`
                const dest = fieldSurveyorSurveyDestination(v)
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => navigate(dest)}
                    className="flex w-full items-center gap-2 rounded-md px-1 py-1.5 text-left hover:bg-slate-50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="fs-row-title truncate">{v.facility_name}</p>
                      <p className="fs-row-meta mt-0.5">{meta}</p>
                    </div>
                    <span className="fs-tag fs-tag-action">{fieldSurveyorSurveyActionLabel(v)}</span>
                  </button>
                )
              })}
              {needsAction.length > 5 && (
                <p className="pt-1 text-center text-[11px] font-bold text-[var(--brand-primary)]">
                  +{needsAction.length - 5} more
                </p>
              )}
            </div>
          </div>
        )}

        {!isLoading && facilities.length === 0 && (
          <div className="fs-surface px-4 py-10 text-center">
            <p className="font-brand text-base font-bold text-[var(--brand-deep)]">
              No facilities yet
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Add a facility to get started.
            </p>
            <button
              type="button"
              onClick={() => navigate('/facilities/new')}
              className="fs-btn-primary mt-4"
            >
              + Add facility
            </button>
          </div>
        )}
      </div>
    </FieldAppShell>
  )
}
